import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiCreditCard, FiTrendingUp, FiTarget, FiBriefcase, FiX, FiChevronRight } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { api } from '../../finance/api/client';

import ExpenseDetailDrawer from '../../finance/pages/expenses/ExpenseDetailDrawer';

const EmployeeFinanceTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    assets: [],
    expenses: [],
    reimbursements: [],
    procurements: [],
    pendingApprovals: []
  });
  const [loading, setLoading] = useState(true);

  // Detail Modal state
  const [activeModal, setActiveModal] = useState(null); // null, 'viewDetail', or 'Expense'
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assetsRes, expensesRes, reimbRes, procRes, workflowsRes] = await Promise.allSettled([
        api.get('/finance/assets/assigned'),
        api.get('/finance/expenses/my'),
        api.get('/finance/reimbursements/my'),
        api.get('/finance/procurement/my'),
        api.get('/finance/workflows/pending')
      ]);

      const getVal = (res) => {
        if (res.status !== 'fulfilled' || !res.value?.data?.data) return [];
        const d = res.value.data.data;
        return Array.isArray(d) ? d : (d?.content || []);
      };

      setData({
        assets: getVal(assetsRes),
        expenses: getVal(expensesRes),
        reimbursements: getVal(reimbRes),
        procurements: getVal(procRes),
        pendingApprovals: getVal(workflowsRes)
      });
    } catch (error) {
      console.error("Error fetching finance data", error);
      toast.error("Failed to load finance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDetail = (item, type) => {
    setSelectedItem({ ...item, _type: type });
    if (type === 'Expense') {
      setActiveModal('Expense');
    } else {
      setActiveModal('viewDetail');
    }
  };

  const StatusBadge = ({ status }) => {
    if (!status) return <span className="px-2 py-0.5 text-xs font-bold rounded bg-slate-100 text-slate-600">UNKNOWN</span>;
    const s = status.toUpperCase();
    if (s === 'APPROVED' || s === 'PAID') return <span className="px-2 py-0.5 text-xs font-bold rounded bg-emerald-50 text-emerald-600">{status}</span>;
    if (s === 'REJECTED') return <span className="px-2 py-0.5 text-xs font-bold rounded bg-red-50 text-red-600">{status}</span>;
    return <span className="px-2 py-0.5 text-xs font-bold rounded bg-blue-50 text-blue-600">{status}</span>;
  };

  const categoriesConfig = [
    { name: 'My Assigned Assets', id: 'Assets', icon: FiBriefcase, items: data.assets, type: 'Asset', path: '/finance/assets' },
    { name: 'My Expenses', id: 'Expenses', icon: FiCreditCard, items: data.expenses, type: 'Expense', path: '/finance/expenses' },
    { name: 'My Reimbursements', id: 'Reimbursements', icon: FiTrendingUp, items: data.reimbursements, type: 'Reimbursement', path: '/finance/reimbursements' },
    { name: 'Procurement Requests', id: 'Procurements', icon: FiTarget, items: data.procurements, type: 'Procurement', path: '/finance/procurement' },
    { name: 'Pending Approvals', id: 'Approvals', icon: FiCheckCircle, items: data.pendingApprovals, type: 'Approval', path: '/finance/approvals' }
  ];

  const Section = ({ category }) => {
    const { name, icon: Icon, items, type, path } = category;
    return (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-center px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <button 
            onClick={() => navigate(path)}
            className="flex items-center gap-2 text-slate-800 font-bold hover:text-blue-600 transition-colors group flex-1 text-left"
          >
            <Icon className="text-blue-600 group-hover:scale-110 transition-transform" /> {name}
            <FiChevronRight className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
          </button>
        </div>
        <div className="p-2 space-y-1 max-h-60 overflow-y-auto custom-scrollbar">
          {items.length === 0 ? (
            <div className="text-xs text-center text-slate-400 py-6 font-medium">No records found</div>
          ) : (
            items.slice(0, 5).map((item, idx) => (
              <div key={idx} onClick={() => openDetail(item, type)} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                <div className="flex-1 min-w-0 pr-3">
                  <p className="text-sm font-bold text-slate-700 truncate">{item.title || item.name || `Request #${item.id || item.entityId}`}</p>
                  <p className="text-xs text-slate-500 truncate">{item.category || item.department || item.entityType || 'General'}</p>
                </div>
                <div className="flex flex-col items-end shrink-0 gap-1">
                  {item.amount || item.purchaseCost ? <span className="text-sm font-bold text-slate-800">${item.amount || item.purchaseCost}</span> : null}
                  <StatusBadge status={item.workflowStatus || item.status} />
                </div>
              </div>
            ))
          )}
          {items.length > 5 && (
            <button 
              onClick={() => navigate(path)}
              className="w-full text-center text-xs font-bold text-blue-600 py-2 hover:bg-blue-50 rounded-lg transition-colors mt-1"
            >
              View all {items.length} items
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-fade-in pb-4 relative">
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {categoriesConfig.map(category => (
            <Section key={category.id} category={category} />
          ))}
        </div>
      )}

      {/* Right Sidebar for View Details (Wider) */}
      {activeModal === 'viewDetail' && (
        <>
          <div 
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity" 
            onClick={() => setActiveModal(null)}
          ></div>
          <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col animate-fade-in border-l border-slate-200">
            {/* Header */}
            <div className="flex justify-between items-center px-8 py-6 border-b border-slate-100 bg-slate-50/80">
              <h3 className="font-bold text-2xl text-slate-800">
                {selectedItem?._type} Details
              </h3>
              <button 
                onClick={() => setActiveModal(null)} 
                className="text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="text-2xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 space-y-8 bg-white">
              {/* Details List */}
              <div className="space-y-4 pt-2">
                <h4 className="font-bold text-xl text-slate-800 px-1">General Information</h4>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                  <div className="divide-y divide-slate-200/60">
                    {Object.entries(selectedItem || {}).filter(([k]) => !['_type', 'id'].includes(k)).map(([key, value]) => {
                      if (typeof value === 'object' || value === null || value === '') return null;
                      return (
                        <div key={key} className="flex justify-between items-center p-4 hover:bg-slate-100/80 transition-colors">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider w-1/3">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className="text-sm font-semibold text-slate-800 text-right break-words max-w-[60%]">
                            {String(value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Action */}
            {selectedItem?._type === 'Approval' && (
              <div className="p-6 border-t border-slate-100 bg-slate-50">
                <button 
                  onClick={() => {
                    setActiveModal(null);
                    navigate('/finance/approvals');
                  }} 
                  className="w-full py-4 bg-slate-800 hover:bg-slate-900 text-white font-bold text-lg rounded-2xl transition-colors shadow-sm"
                >
                  Go to Approvals Inbox
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Admin Expense Drawer for User Space Expenses */}
      {activeModal === 'Expense' && selectedItem && (
        <ExpenseDetailDrawer
          open={true}
          expenseId={selectedItem.id}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
};

export default EmployeeFinanceTab;
